import type { IProductItem } from 'src/types/product';

import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import { Button, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useAppDispatch } from 'src/context/store';
import { parentLikeActivityMutationFunc } from 'src/context/like/parent_likeActivity';
import { parentUnlikeActivityMutationFunc } from 'src/context/like/parent_unlikeActivity';

import { Image } from 'src/components/image';
import { Iconify } from 'src/components/iconify';
import { Lightbox, useLightBox } from 'src/components/lightbox';
import {
  Carousel,
  useCarousel,
  CarouselThumb,
  CarouselThumbs,
  CarouselArrowNumberButtons,
} from 'src/components/carousel';

// ----------------------------------------------------------------------

type Props = {
  images?: IProductItem['images'];
  product: any;
};

export function ProductDetailsCarousel({ images, product }: Props) {
  const dispatch = useAppDispatch();

  const router = useRouter();
  const { accessToken } = useSelector((state: any) => state.auth);
  const [hasAccessToken, setHasAccessToken] = useState(false);
  const [isLikedActivity, setIsLikedActivity] = useState(false);
  const carousel = useCarousel({ thumbs: { slidesToShow: 'auto' } });

  const slides = images?.map((img) => ({ src: img })) || [];

  const lightbox = useLightBox(slides);

  useEffect(() => {
    if (lightbox.open) {
      carousel.mainApi?.scrollTo(lightbox.selected, true);
    }
  }, [carousel.mainApi, lightbox.open, lightbox.selected]);

  useEffect(() => {
    setHasAccessToken(accessToken);
  }, [accessToken]);

  useEffect(() => {
    setIsLikedActivity(product?.likedByParent);
  }, [product]);

  return (
    <>
      <div>
        <Box sx={{ mb: 2.5, position: 'relative', color: 'common.white' }}>
          <Button
            variant="outlined"
            color="inherit"
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              minWidth: 0,
              width: 56,
              height: 56,
              zIndex: 9,
              borderWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              // borderRadius: 2,
              // borderColor: 'common.white',
            }}
            onClick={() => {
              if (hasAccessToken) {
                if (isLikedActivity) {
                  dispatch(
                    parentUnlikeActivityMutationFunc({
                      activityId: product._id,
                    })
                  );
                  setIsLikedActivity(false);
                } else {
                  dispatch(
                    parentLikeActivityMutationFunc({
                      activityId: product._id,
                    })
                  );
                  setIsLikedActivity(true);
                }
              } else {
                router.push(paths.auth.jwt.signIn);
              }
            }}
          >
            <Iconify
              icon={
                isLikedActivity
                  ? 'material-symbols:star-rounded'
                  : 'material-symbols:star-outline-rounded'
              }
              width={32}
              sx={{
                textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)',
              }}
            />
            <Typography
              variant="caption"
              fontFamily="Rubik"
              fontSize={12}
              fontWeight={600}
              sx={{
                color: 'common.white',
                cursor: 'pointer',
                textShadow: '0px 0px 10px rgba(0, 0, 0, 0.5)',
              }}
            >
              {isLikedActivity ? 'Favoriden Çıkar' : 'Favoriye Ekle'}
            </Typography>
          </Button>

          <CarouselArrowNumberButtons
            {...carousel.arrows}
            options={carousel.options}
            totalSlides={carousel.dots.dotCount}
            selectedIndex={carousel.dots.selectedIndex + 1}
            sx={{ right: 16, bottom: 16, position: 'absolute' }}
          />

          <Carousel carousel={carousel} sx={{ borderRadius: 2 }}>
            {slides.map((slide, index) => (
              <Image
                key={index}
                alt={slide.src}
                src={slide.src}
                ratio="1/1"
                onClick={() => lightbox.onOpen(slide.src)}
                sx={{ cursor: 'zoom-in', minWidth: 320 }}
              />
            ))}
          </Carousel>
        </Box>

        <CarouselThumbs
          ref={carousel.thumbs.thumbsRef}
          options={carousel.options?.thumbs}
          slotProps={{ disableMask: true }}
          sx={{ width: 360 }}
          data-description-section
        >
          {slides.map((item, index) => (
            <CarouselThumb
              key={index}
              index={index}
              src={item.src}
              selected={index === carousel.thumbs.selectedIndex}
              onClick={() => carousel.thumbs.onClickThumb(index)}
            />
          ))}
        </CarouselThumbs>
      </div>

      <Lightbox
        index={lightbox.selected}
        slides={slides}
        open={lightbox.open}
        close={lightbox.onClose}
        onGetCurrentIndex={(index) => lightbox.setSelected(index)}
      />
    </>
  );
}
